// src/components/common/ThemeSettings/ThemeSettings.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Palette, Sun, Moon, Check, Search, Filter } from 'lucide-react';
import { useTheme } from '../../../hooks/useTheme';
import { useAuth } from '../../../hooks/useAuth';
import { useAlert } from '../../../contexts/AlertContext';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './ThemeSettings.module.css';

const ThemeSettings = ({ onNavigationAttempt }) => {
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  const { user, getUserPermissions, refreshUser } = useAuth();
  const { 
    currentTheme, 
    availableThemes, 
    setTheme,
    previewTheme,
    getTheme,
    getCategories 
  } = useTheme();

  const [selectedTheme, setSelectedTheme] = useState(currentTheme);
  const [savedTheme, setSavedTheme] = useState(currentTheme); // Track the actual saved theme
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('all'); // 'all', 'light', 'dark'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Get all categories
  const categories = getCategories();

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return selectedTheme !== savedTheme;
  }, [selectedTheme, savedTheme]);

  // Filter themes based on search and filters
  const filteredThemes = useMemo(() => {
    return availableThemes.filter(theme => {
      // Search filter
      const matchesSearch = theme.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           theme.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Mode filter (light/dark)
      const matchesMode = modeFilter === 'all' || 
                         (modeFilter === 'light' && !theme.isDark) ||
                         (modeFilter === 'dark' && theme.isDark);
      
      // Category filter
      const matchesCategory = categoryFilter === 'all' || theme.category === categoryFilter;

      return matchesSearch && matchesMode && matchesCategory;
    });
  }, [availableThemes, searchQuery, modeFilter, categoryFilter]);

  // Check permission - AFTER all hooks
  const hasPermission = getUserPermissions().includes('manage_subscription');

  // Handle navigation attempts when there are unsaved changes
  const handleNavigationAttempt = useCallback(async () => {
    if (!hasUnsavedChanges) {
      return true; // Allow navigation
    }

    const shouldDiscard = await confirm(
      t('subscription.unsavedThemeChanges'),
      {
        confirmText: t('subscription.discardChanges'),
        cancelText: t('subscription.stayOnPage')
      }
    );

    if (shouldDiscard) {
      // Revert to saved theme before allowing navigation
      previewTheme(savedTheme);
      setSelectedTheme(savedTheme);
      return true;
    }

    return false; // Prevent navigation
  }, [hasUnsavedChanges, confirm, t, previewTheme, savedTheme]);

  // Expose navigation handler to parent component
  useEffect(() => {
    if (onNavigationAttempt) {
      onNavigationAttempt(handleNavigationAttempt);
    }
  }, [onNavigationAttempt, handleNavigationAttempt]);

  // Initialize saved theme when currentTheme changes (e.g., on page load)
  useEffect(() => {
    setSavedTheme(currentTheme);
    setSelectedTheme(currentTheme);
  }, [currentTheme]);

  // Cleanup: revert to saved theme if component unmounts with unsaved changes
  useEffect(() => {
    return () => {
      if (hasUnsavedChanges) {
        // Revert to saved theme on unmount
        previewTheme(savedTheme);
      }
    };
  }, [hasUnsavedChanges, savedTheme, previewTheme]);

  // Handle theme selection - Apply preview immediately
  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    // Apply theme as live preview (no persistence)
    previewTheme(themeId);
  };

  // Handle cancel - Revert to saved theme
  const handleCancel = () => {
    setSelectedTheme(savedTheme);
    // Revert preview to saved theme
    previewTheme(savedTheme);
  };

  // Handle save with confirmation
  const handleSave = () => {
    if (selectedTheme === savedTheme) {
      error(t('subscription.themeAlreadySelected'));
      return;
    }
    setShowConfirmDialog(true);
  };

  // Confirm theme change - Persist to backend and localStorage
  const confirmThemeChange = async () => {
    setShowConfirmDialog(false);
    setIsLoading(true);

    try {
      // setTheme with persist=true will save to backend and localStorage
      const result = await setTheme(selectedTheme, true);
      
      if (result) {
        // Update saved theme to match selected theme
        setSavedTheme(selectedTheme);
        
        // Refresh user data to get updated theme from backend
        await refreshUser();
        
        const themeName = availableThemes.find(t => t.id === selectedTheme)?.name;
        success(t('subscription.themeUpdatedSuccess', { theme: themeName }));
      } else {
        error(t('subscription.themeUpdateFailed'));
        // Revert to saved theme
        setSelectedTheme(savedTheme);
        previewTheme(savedTheme);
      }
    } catch (err) {
      console.error('Theme update error:', err);
      error(t('subscription.themeUpdateError'));
      // Revert to saved theme
      setSelectedTheme(savedTheme);
      previewTheme(savedTheme);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel confirmation - Keep preview
  const cancelThemeChange = () => {
    setShowConfirmDialog(false);
  };

  const currentThemeData = getTheme();
  const selectedThemeData = availableThemes.find(t => t.id === selectedTheme);

  // Render permission check AFTER all hooks
  if (!hasPermission) {
    return (
      <div className={styles.noPermission}>
        <Palette size={48} />
        <h3>{t('common.accessDenied')}</h3>
        <p>{t('subscription.themePermissionRequired')}</p>
      </div>
    );
  }

  return (
    <div className={styles.themeSettings}>
      {/* Current Theme Display */}
      <div className={styles.currentTheme}>
        <div className={styles.currentThemeHeader}>
          <div className={styles.currentThemeInfo}>
            <Palette size={20} />
            <div>
              <h3>{t('subscription.currentTheme')}</h3>
              <p>{currentThemeData.name}</p>
            </div>
          </div>
          <div className={styles.currentThemePreview}>
            <div 
              className={styles.colorSwatch}
              style={{ background: currentThemeData.primary }}
              title={t('subscription.primaryColor')}
            />
            <div 
              className={styles.colorSwatch}
              style={{ background: currentThemeData.secondary }}
              title={t('subscription.secondaryColor')}
            />
            <div className={styles.themeBadge}>
              {currentThemeData.isDark ? (
                <>
                  <Moon size={12} />
                  <span>{t('subscription.dark')}</span>
                </>
              ) : (
                <>
                  <Sun size={12} />
                  <span>{t('subscription.light')}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <p className={styles.currentThemeDescription}>
          {currentThemeData.description}
        </p>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBar}>
          <Search size={18} />
          <input
            type="text"
            placeholder={t('subscription.searchThemes')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterButtons}>
          <div className={styles.filterGroup}>
            <Filter size={16} />
            <select
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">{t('subscription.allModes')}</option>
              <option value="light">{t('subscription.lightOnly')}</option>
              <option value="dark">{t('subscription.darkOnly')}</option>
            </select>
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">{t('subscription.allColors')}</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Theme Grid */}
      <div className={styles.themeGrid}>
        {filteredThemes.map(theme => (
          <div
            key={theme.id}
            className={`
              ${styles.themeCard} 
              ${selectedTheme === theme.id ? styles.selected : ''}
              ${savedTheme === theme.id ? styles.current : ''}
            `}
            onClick={() => handleThemeSelect(theme.id)}
          >
            {/* Selection indicator */}
            {selectedTheme === theme.id && (
              <div className={styles.selectedIndicator}>
                <Check size={16} />
              </div>
            )}

            {/* Current theme badge */}
            {savedTheme === theme.id && (
              <div className={styles.currentBadge}>
                {t('subscription.current')}
              </div>
            )}

            {/* Color preview */}
            <div className={styles.themePreview}>
              <div 
                className={styles.primaryColor}
                style={{ background: theme.primary }}
              />
              <div 
                className={styles.secondaryColor}
                style={{ background: theme.secondary }}
              />
            </div>

            {/* Theme info */}
            <div className={styles.themeInfo}>
              <h4>{theme.name}</h4>
              <div className={styles.themeMetadata}>
                <span className={styles.themeCategory}>
                  {theme.category.charAt(0).toUpperCase() + theme.category.slice(1)}
                </span>
                <span className={`${styles.themeMode} ${theme.isDark ? styles.dark : styles.light}`}>
                  {theme.isDark ? <Moon size={12} /> : <Sun size={12} />}
                  {theme.isDark ? t('subscription.dark') : t('subscription.light')}
                </span>
              </div>
              <p className={styles.themeDescription}>{theme.description}</p>
            </div>
          </div>
        ))}

        {filteredThemes.length === 0 && (
          <div className={styles.noResults}>
            <Palette size={48} />
            <p>{t('subscription.noThemesFound')}</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button
          className={styles.cancelButton}
          onClick={handleCancel}
          disabled={!hasUnsavedChanges || isLoading}
        >
          {t('common.cancel')}
        </button>
        <button
          className={styles.saveButton}
          onClick={handleSave}
          disabled={!hasUnsavedChanges || isLoading}
        >
          {isLoading ? t('common.saving') : t('common.saveChanges')}
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={styles.dialogOverlay} onClick={cancelThemeChange}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <Palette size={24} />
              <h3>{t('subscription.confirmThemeChange')}</h3>
            </div>
            
            <div className={styles.dialogContent}>
              <p>{t('subscription.themeChangeConfirmation')}</p>
              
              <div className={styles.themeComparison}>
                <div className={styles.comparisonItem}>
                  <span className={styles.comparisonLabel}>{t('subscription.from')}:</span>
                  <div className={styles.comparisonTheme}>
                    <div 
                      className={styles.colorSwatch}
                      style={{ background: currentThemeData.primary }}
                    />
                    <span>{currentThemeData.name}</span>
                  </div>
                </div>
                
                <div className={styles.comparisonArrow}>→</div>
                
                <div className={styles.comparisonItem}>
                  <span className={styles.comparisonLabel}>{t('subscription.to')}:</span>
                  <div className={styles.comparisonTheme}>
                    <div 
                      className={styles.colorSwatch}
                      style={{ background: selectedThemeData?.primary }}
                    />
                    <span>{selectedThemeData?.name}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.dialogActions}>
              <button
                className={styles.dialogCancelButton}
                onClick={cancelThemeChange}
              >
                {t('common.cancel')}
              </button>
              <button
                className={styles.dialogConfirmButton}
                onClick={confirmThemeChange}
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeSettings;